import React, { useState, useEffect } from 'react';
import useWayfinderSync from '../hooks/useWayfinderSync';
import '../styles/wayfinder.css';

/**
 * Wayfinder Component
 * Displays occupied desks by wing with real-time updates
 * Users can search by name or select all desks on a wing
 * 
 * Props:
 * - onDesksLoad: callback when desks are loaded
 * - onDeskSelect: callback when desk is selected
 */
function Wayfinder({ onDesksLoad, onDeskSelect }) {
  const [activeWing, setActiveWing] = useState('EAST');
  const { occupiedDesks, positions, loading, error } = useWayfinderSync(activeWing, 180000);
  const [selectedDesk, setSelectedDesk] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (onDesksLoad && occupiedDesks.length > 0) {
      onDesksLoad({ desks: occupiedDesks, positions });
    }
  }, [occupiedDesks, positions, onDesksLoad]);

  const handleDeskClick = (desk) => {
    setSelectedDesk(desk.desk_name);

    // Emit event for SVG highlighting
    window.dispatchEvent(
      new CustomEvent('highlightDesk', {
        detail: { deskName: desk.desk_name, position: desk.position }
      })
    );

    // Call callback if provided
    if (onDeskSelect) {
      onDeskSelect(desk);
    }
  };

  const filteredDesks = occupiedDesks.filter(desk =>
    desk.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    desk.desk_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Debug logging
  useEffect(() => {
    if (occupiedDesks.length > 0) {
      console.log(`[Wayfinder] ${activeWing}: ${occupiedDesks.length} desks loaded`);
      console.log('[Wayfinder] Sample desk:', occupiedDesks[0]);
      console.log(`[Wayfinder] Filtered: ${filteredDesks.length} desks for "${searchTerm}"`);
    }
  }, [occupiedDesks, filteredDesks, searchTerm, activeWing]);

  if (error) {
    return (
      <div className="wayfinder-container error">
        <div className="error-icon">⚠️</div>
        <p className="error-text">{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="wayfinder-container loading">
        <div className="spinner"></div>
        <p>Loading wayfinder...</p>
      </div>
    );
  }

  return (
    <div className="wayfinder-container">
      {/* Wing Tabs */}
      <div className="wayfinder-tabs">
        <button
          className={`wing-tab ${activeWing === 'EAST' ? 'active' : ''}`}
          onClick={() => setActiveWing('EAST')}
        >
          <span className="wing-icon">🏢</span> EAST WING
        </button>
        <button
          className={`wing-tab ${activeWing === 'WEST' ? 'active' : ''}`}
          onClick={() => setActiveWing('WEST')}
        >
          <span className="wing-icon">🏢</span> WEST WING
        </button>
      </div>

      {occupiedDesks.length > 0 && (
        <div className="wayfinder-search">
          <input
            type="text"
            placeholder="🔍 Search by colleague name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      )}

      <div className="wayfinder-list">
        {occupiedDesks.length === 0 ? (
          <div className="no-occupancy">
            <p>😴 No one in {activeWing} wing right now</p>
          </div>
        ) : filteredDesks.length === 0 ? (
          <div className="no-search-results">
            <p>No results found for "{searchTerm}" in {activeWing} wing</p>
          </div>
        ) : (
          filteredDesks.map(desk => (
            <div
              key={desk.desk_name}
              className={`desk-item ${selectedDesk === desk.desk_name ? 'highlighted' : ''}`}
              onClick={() => handleDeskClick(desk)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => e.key === 'Enter' && handleDeskClick(desk)}
            >
              <div className="desk-item-avatar">
                {desk.avatar ? (
                  <img
                    src={desk.avatar}
                    alt={desk.user_name}
                    className="user-avatar"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="avatar-placeholder"
                  style={{ display: desk.avatar ? 'none' : 'flex' }}
                >
                  {desk.user_name ? desk.user_name.charAt(0).toUpperCase() : '?'}
                </div>
              </div>

              <div className="desk-info">
                <p className="user-name">{desk.user_name}</p>
                <span className="desk-separator">•</span>
                <p className="desk-name">{desk.desk_name}</p>
                <span className="desk-separator">•</span>
                {desk.slot && <p className="time-slot">{desk.slot}</p>}
              </div>

              <div className="desk-item-action">
                <span className="arrow-icon">🗺️</span>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="wayfinder-footer">
        <small>Last updated: {new Date().toLocaleTimeString()}</small>
      </div>
    </div>
  );
}

export default Wayfinder;
